import { useMemo } from 'react';
import { useLocation, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { formatNumberWithCommas } from '@/modules/common/utils/format-numbers.ts';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import IssuerRegisterClassDropDown from '@/modules/issuer-dashboard/components/IssuerRegisterClassDropDown.tsx';
import { useIssuerRegisterClassListQuery } from '@/modules/issuer-dashboard/queries/registers.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { BodyCell, InfoCard, Table, TableBodyColumn, TableHeadColumn } from 'ui';
import { useIsMobile } from 'ui/hooks';

type TypeHeadRegister =
  | 'Class'
  | 'Price'
  | 'Min. Investment'
  | 'Current asset value'
  | 'Total holders'
  | 'Units held'
  | '';

const headItems: TableHeadColumn<TypeHeadRegister>[] = [
  { title: 'Class', className: 'min-w-[170px] !py-2' },
  { title: 'Price', className: '!py-2' },
  { title: 'Units held', className: '!py-2' },
  { title: 'Current asset value', className: '!py-2' },
  { title: 'Min. Investment', className: '!py-2' },
  { title: 'Total holders', className: '!py-2' },
  { title: '', className: '!py-2' },
];

export const SecurityClasses = () => {
  const [params] = useSearchParams();
  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { registerId } = useOutletContext<IssuerDashboardWrapperOutletContext>();

  const {
    data: registerClassList,
    isFetching,
    isError,
  } = useIssuerRegisterClassListQuery({ search: undefined }, Number(registerId));

  const bodyItems = useMemo<TableBodyColumn[]>(() => {
    if (!registerClassList?.registers?.length) {
      return [];
    }

    return registerClassList?.registers?.map(data => {
      const body: BodyCell[] = [
        {
          cell: (
            <span className="text-sm leading-[22px] text-[#0369A1] font-[500]">
              {data.class_name}
            </span>
          ),
          className: '!py-2',
        },
        {
          cell: `$${formatNumberWithCommas(Math.round(data.current_price_per_share * 100) / 100)}`,
          className: '!py-2',
        },
        {
          cell: formatNumberWithCommas(data.total_units),
          className: '!py-2',
        },
        {
          cell: `$${formatNumberWithCommas(Math.round(data.total_invested * 100) / 100)}`,
          className: '!py-2',
        },
        {
          cell: `$${formatNumberWithCommas(data.min_investment)}`,
          className: '!py-2',
        },
        {
          cell: formatNumberWithCommas(data.total_holders),
          className: '!py-2',
        },
        // { cell: goodFormatUSDCurrency(Math.round(data.total_invested * 100) / 100) },
        {
          cell: <IssuerRegisterClassDropDown registerClass={data} placement="left-end" />,
          className: '!py-2',
        },
      ];

      return {
        cells: body,
        onClick: () =>
          navigate(
            {
              pathname: issuerDashboardPaths.dynamic.classById(String(data.id)),
              search: params.toString(),
            },
            {
              state: { from: pathname + '?' + params.toString() },
            },
          ),
      };
    });
  }, [navigate, params, pathname, registerClassList?.registers]);

  return (
    <InfoCard title="Security classes" classes={{ content: '!p-0 !pt-6' }}>
      <Table
        className="my-0 border-t rounded-b-xl"
        classNameHead="border-t-none"
        withRounded={false}
        withBorderX={isMobile}
        withBorder={!isMobile}
        headItems={headItems}
        items={bodyItems}
        isLoading={isFetching}
        isError={isError}
        mobileFirstCellShowInHeadLeft={true}
        mobileLastCellShowInHeadRight={true}
      />
    </InfoCard>
  );
};
